import React, { FC, useEffect, useState } from "react"
import {
    Button,
    Dialog,
    DialogHeader,
    DialogDescription,
    Input,
    Grid,
    GridRow,
    useIntl,
    Icon,
    DialogActions,
    DialogContent,
} from "@metsooutotec/modes-react-components"
import customerStore from "features/organizations/customers/store/customer-store"
import { validateEmail, validateText } from "validators/inputValidators"
import { NavigationModalOptions, resetRecordFormData } from "components/utils/modal-helper"
import { ModalTypes, validateCustomerForm } from "../utils/customer-modal-helper"
import { CreateCustomer, Customer, EditCustomer } from "../models/customer-models"
import { Entity } from "features/organizations/common/models/organization-shared-models"
import { observer } from "mobx-react-lite"

interface CustomerModalProps {
    isOpen?: boolean
    onClose: (passed: boolean, customerId?: string, navOptions?: NavigationModalOptions) => void
    customer?: Customer
}

const CustomerModal: FC<CustomerModalProps> = observer(({ isOpen, onClose, customer }) => {
    const { formatMessage } = useIntl()
    const isEditing = Boolean(customer?.id)
    const { customerLoading, createEntity, editEntity } = customerStore
    const [formData, setFormData] = useState<Record<string, string>>({
        customerName: "",
        contact: "",
        metsoContact: "",
    })

    const [formErrors, setFormErrors] = useState<Record<string, string>>({})
    const [formModified, setFormModified] = useState(false)
    const [errorMessage, setErrorMessage] = useState("")

    useEffect(() => {
        if (customer) {
            setFormData({
                customerName: customer.name || "",
                contact: customer.contact || "",
                metsoContact: customer.metsoContact || "",
            })
        } else {
            resetRecordFormData(setFormData)
        }

        resetRecordFormData(setFormErrors)
        setFormModified(false)
        setErrorMessage("")
    }, [customer])

    const labels = {
        name: formatMessage("customerModal.name"),
        contact: formatMessage("customerModal.contact"),
        metsoContact: formatMessage("customerModal.metsoContact"),
        confirm: formatMessage("modal.confirm"),
        cancel: formatMessage("modal.cancel"),
        header: isEditing ? formatMessage("customerModal.headerEdit") : formatMessage("customerModal.header"),
        description: formatMessage("modal.description"),
    }

    const inputConfig = [
        { id: "customerName", label: labels.name, type: "text", required: true, validate: validateText },
        { id: "contact", label: labels.contact, type: "email", required: false, validate: validateEmail },
        { id: "metsoContact", label: labels.metsoContact, type: "email", required: true, validate: validateEmail },
    ] as const

    const handleChange = (id: string, value: string) => {
        setFormData(prev => ({ ...prev, [id]: value }))
        setFormModified(true)
        setFormErrors(prev => ({ ...prev, [id]: "" }))
        setErrorMessage("")
    }

    const handleValidation = (id: string, error?: string) => {
        if (error) {
            setFormErrors(prev => ({ ...prev, [id]: error }))
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (isEditing && !formModified) {
            setErrorMessage("No changes made.")
            return
        }

        const errors = validateCustomerForm(formData)
        const hasErrors = Object.values(errors).some(Boolean)

        if (hasErrors) {
            setFormErrors(errors)
            setErrorMessage("Please fix validation errors.")
            return
        }

        const newCustomer: Entity = {
            name: formData.customerName,
            contact: formData.contact,
            metsoContact: formData.metsoContact,
        }

        const result = isEditing
            ? await editEntity({ ...newCustomer, id: customer!.id } as EditCustomer, ModalTypes.CustomerModal)
            : await createEntity(newCustomer as CreateCustomer, ModalTypes.CustomerModal)

        if (!result.passed) {
            setErrorMessage(result.message)
            return
        }

        onClose(true, result.entityId, { type: isEditing ? "edited" : "created" })
    }

    const onClearClick = (id: string) => {
        setFormData(prev => ({ ...prev, [id]: "" }))
        setFormErrors(prev => ({ ...prev, [id]: "" }))
    }

    return (
        <Grid>
            <Dialog dimmer open={isOpen} size='small' style={{ maxWidth: "512px" }}>
                <DialogHeader username=''>{labels.header}</DialogHeader>
                {errorMessage && <p style={{ color: "red" }}>{errorMessage}</p>}
                <DialogDescription style={{ marginBottom: "var(--size-xxl)" }}>{labels.description}</DialogDescription>
                <DialogContent>
                    {inputConfig.map(({ id, label, type, required, validate }) => (
                        <GridRow key={id} style={{ marginBottom: "var(--size-m)" }}>
                            <Input
                                id={id}
                                label={label}
                                placeholder={`Type ${label}...`}
                                type={type}
                                value={formData[id]}
                                error={formErrors[id]}
                                required={required}
                                uiRequired={required}
                                heightClass='medium'
                                spanning
                                validate={required || formData[id] ? validate : undefined}
                                onValidation={required || formData[id] ? e => handleValidation(id, e) : undefined}
                                onChange={e => handleChange(id, e.target.value)}
                                icon={formData[id] ? <Icon className='icon-cross-circle-fill-16' onClick={() => onClearClick(id)} /> : undefined}
                            />
                        </GridRow>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button loading={customerLoading} disabled={customerLoading} onClick={handleSubmit} centered primary size='medium'>
                        {labels.confirm}
                    </Button>

                    <Button onClick={() => onClose(false)} centered size='medium'>
                        {labels.cancel}
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    )
})

export default CustomerModal
